import type { MultiplicationFactId } from "./model.ts";

export interface MultiplicationAttempt {
  readonly id: string;
  readonly profileId: string;
  readonly sessionId: string;
  readonly presentationId: string;
  readonly factId: MultiplicationFactId;
  readonly attemptOrdinal: number;
  readonly presentedAt: string;
  readonly firstInputAt: string | null;
  readonly submittedAt: string;
  readonly enteredAnswer: number | null;
  readonly correct: boolean | null;
  readonly helpRequested: boolean;
  readonly supportUsed: boolean;
  readonly editCount: number;
  readonly thinkingTimeMs: number | null;
  readonly totalTimeMs: number;
}

export interface AttemptValidationIssue {
  readonly code: string;
  readonly message: string;
}

export function validateMultiplicationAttempt(
  attempt: MultiplicationAttempt,
): AttemptValidationIssue[] {
  const issues: AttemptValidationIssue[] = [];
  const presentedAt = Date.parse(attempt.presentedAt);
  const submittedAt = Date.parse(attempt.submittedAt);
  const firstInputAt = attempt.firstInputAt === null ? null : Date.parse(attempt.firstInputAt);

  if (!Number.isFinite(presentedAt) || !Number.isFinite(submittedAt)) {
    issues.push({ code: "INVALID_TIMESTAMP", message: "De poging heeft ongeldige tijden." });
  } else if (submittedAt < presentedAt) {
    issues.push({ code: "TIMESTAMP_ORDER", message: "Een antwoord kan niet voor de opgave liggen." });
  }

  if (firstInputAt !== null && (
    !Number.isFinite(firstInputAt)
    || firstInputAt < presentedAt
    || firstInputAt > submittedAt
  )) {
    issues.push({ code: "FIRST_INPUT_ORDER", message: "De eerste invoer ligt buiten de poging." });
  }

  if (!Number.isInteger(attempt.attemptOrdinal) || attempt.attemptOrdinal < 1) {
    issues.push({ code: "INVALID_ORDINAL", message: "Pogingen beginnen bij één." });
  }
  if (!Number.isInteger(attempt.editCount) || attempt.editCount < 0) {
    issues.push({ code: "INVALID_EDIT_COUNT", message: "Het aantal wijzigingen is ongeldig." });
  }
  if (attempt.helpRequested && (attempt.enteredAnswer !== null || attempt.correct !== null)) {
    issues.push({ code: "HELP_IS_NOT_ANSWER", message: "Een hulpvraag is geen antwoord." });
  }
  if (!attempt.helpRequested && (attempt.enteredAnswer === null || attempt.correct === null)) {
    issues.push({ code: "ANSWER_INCOMPLETE", message: "Een antwoord mist invoer of beoordeling." });
  }
  if (attempt.supportUsed && attempt.attemptOrdinal === 1 && !attempt.helpRequested) {
    issues.push({ code: "EARLY_SUPPORT", message: "Een eerste antwoord kan nog geen herstelsteun gebruiken." });
  }

  return issues;
}

export function isIndependentResponse(attempt: MultiplicationAttempt): boolean {
  return attempt.attemptOrdinal === 1
    && !attempt.helpRequested
    && !attempt.supportUsed
    && attempt.enteredAnswer !== null
    && attempt.correct !== null;
}

export function isCleanIndependentSuccess(attempt: MultiplicationAttempt): boolean {
  return isIndependentResponse(attempt)
    && attempt.correct === true
    && attempt.editCount === 0;
}
